import { useCallback, useContext, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import SensorsIcon from "@mui/icons-material/Sensors";
import BlockIcon from "@mui/icons-material/Block";
import StartFyreModal from "../components/StartFyreModal";
import StartYTTranscribeModal from "../components/StartYTTranscribeModal";
import StartSpacesTranscribeModal from "../components/StartSpacesTranscribeModal";
import { useSnackbar } from "../components/AlertNotification";
import DropAnythingContext from "../context/DropAnythingContext";
import { RootContext } from "../context/root-provider";
import useFeatureFlag from "../hooks/useFeatureFlag";
import useModalOpen from "../hooks/useModalOpen";
import { useDroppedLink } from "../vendor/liveTapLinks/useDroppedLink";
import { SOURCE_LINK_KIND, isMeetingKind, readClipboardSourceLink } from "../vendor/liveTapLinks/sourceLink";
import styles from "./DropAnything.module.scss";

/**
 * Drop anything (TT-1231), app-wide: a link dropped or pasted on any signed-in
 * page opens the start modal for its kind with the URL in the field. Meeting
 * links start through Live; YouTube and X Space have starts of their own,
 * behind the team feature like their buttons. The start modals live here,
 * once, and the pages' start buttons reach them through the context.
 *
 * Stands down, by decision (RR, 2026-10-08): in Settings, while any modal is
 * open, and on a live tap page while that tap is running — a drop must not
 * start a second live meeting over one that is already there. Standing down
 * still catches the drop: with no handler the browser would navigate the tab
 * to the dropped URL, off the live tap. The card says why instead.
 */
export default function DropAnythingProvider({ children }) {
  const { userInfo } = useContext(RootContext);
  const { showSnackbar, snackbarEl } = useSnackbar();
  const tapestryTeamFeature = useFeatureFlag(userInfo);
  const { pathname } = useLocation();
  const modalOpen = useModalOpen();
  const [liveConflict, setLiveConflict] = useState(false);

  const [startFyreModalOpen, setStartFyreModalOpen] = useState(false);
  const [ytTranscribeModalOpen, setYTTranscribeModalOpen] = useState(false);
  const [spacesTranscribeModalOpen, setSpacesTranscribeModalOpen] = useState(false);
  // The link a modal opens on, `{ kind, url, label }`: set on every open, null
  // when the open came from a button with nothing to prefill.
  const [presetLink, setPresetLink] = useState(null);

  // What to call after a start: the documents page's list, while it is up.
  const refreshRef = useRef(null);
  const registerRefresh = useCallback((fn) => {
    refreshRef.current = fn;
    return () => {
      if (refreshRef.current === fn) refreshRef.current = null;
    };
  }, []);
  const refresh = useCallback(() => refreshRef.current?.(), []);

  const openStartModal = useCallback((kind, link = null) => {
    setPresetLink(link);
    if (kind === SOURCE_LINK_KIND.YOUTUBE) setYTTranscribeModalOpen(true);
    else if (kind === SOURCE_LINK_KIND.XSPACE) setSpacesTranscribeModalOpen(true);
    else setStartFyreModalOpen(true);
  }, []);

  // Zoom is a meeting kind with no start from a link: live Zoom capture runs
  // inside the Zoom client only. The others start here.
  const canStart = useCallback(
    (kind) =>
      (isMeetingKind(kind) && kind !== SOURCE_LINK_KIND.ZOOM) ||
      (tapestryTeamFeature && (kind === SOURCE_LINK_KIND.YOUTUBE || kind === SOURCE_LINK_KIND.XSPACE)),
    [tapestryTeamFeature]
  );

  // A link dropped or pasted on the page.
  const openForLink = useCallback(
    (link) => {
      if (!link.kind) {
        showSnackbar("No live source for that link. Google Meet, Microsoft Teams, YouTube and X Space links start one.", "info");
      } else if (link.kind === SOURCE_LINK_KIND.ZOOM) {
        showSnackbar("Zoom live capture starts inside the Zoom client, not from a link.", "info");
      } else if (!canStart(link.kind)) {
        showSnackbar(`${link.label} transcription isn't enabled for your account.`, "info");
      } else {
        openStartModal(link.kind, link);
      }
    },
    [canStart, openStartModal, showSnackbar]
  );

  // A start button. The clipboard is scanned first: a link it holds that this
  // button starts (any startable link, for Live) is already in the field when
  // the modal opens. A refused or empty read just opens the modal as before.
  const startFromButton = useCallback(
    async (kind = null) => {
      const link = await readClipboardSourceLink();
      const prefill = link && canStart(link.kind) && (kind === null || link.kind === kind);
      if (prefill) openStartModal(link.kind, link);
      else openStartModal(kind);
    },
    [canStart, openStartModal]
  );

  // Why a drop is refused right now, or null when it would start something.
  const standDown = pathname.startsWith("/settings")
    ? "Links don't start from Settings. Go back to your documents and drop it there."
    : modalOpen
    ? "Close the dialog first, then drop the link."
    : liveConflict
    ? "This live tap is running. A dropped link would start another one over it."
    : null;
  const refuse = useCallback(() => showSnackbar(standDown, "warning"), [showSnackbar, standDown]);
  const { dragging } = useDroppedLink({
    onLink: standDown ? refuse : openForLink,
    enabled: !!userInfo,
  });

  const value = useMemo(
    () => ({ startFromButton, openStartModal, registerRefresh, setLiveConflict }),
    [startFromButton, openStartModal, registerRefresh]
  );

  return (
    <DropAnythingContext.Provider value={value}>
      {children}
      {dragging && (
        <div className={styles.linkDropOverlay} aria-hidden="true">
          <div className={`${styles.linkDropCard} ${standDown ? styles.linkDropCardStandDown : ""}`}>
            {standDown ? <BlockIcon /> : <SensorsIcon />}
            {standDown || "Drop the link to start a live Tapestry"}
          </div>
        </div>
      )}
      <StartFyreModal
        open={startFyreModalOpen}
        setOpen={setStartFyreModalOpen}
        presetJoinUrl={presetLink?.url}
        refreshCallback={refresh}
        showSnackbar={showSnackbar}
      />
      <StartYTTranscribeModal
        open={ytTranscribeModalOpen}
        setOpen={setYTTranscribeModalOpen}
        presetUrl={presetLink?.url}
        showSnackbar={showSnackbar}
        refreshCallback={refresh}
      />
      <StartSpacesTranscribeModal
        open={spacesTranscribeModalOpen}
        setOpen={setSpacesTranscribeModalOpen}
        presetUrl={presetLink?.url}
        showSnackbar={showSnackbar}
        refreshCallback={refresh}
      />
      {snackbarEl}
    </DropAnythingContext.Provider>
  );
}

DropAnythingProvider.propTypes = {
  children: PropTypes.node,
};
